"use client";

import { DealLabel, FavoriteButton, PriceTrackingButton } from "@/components";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useImpressionTracking } from "@/hooks/useImpressionTracking";
import { type Listing } from "@/services/api";
import { evaluateDealType } from "@/utils/dealEvaluation";
import { formatBrandName } from "@/utils/helpers/stringHelpers";
import Image from "next/image";
import React, { useMemo } from "react";

interface ListingsGridCardProps {
  listing: Listing;
  showDealLabel?: boolean;
  position?: number;
}

const ListingsGridCard: React.FC<ListingsGridCardProps> = ({
  listing,
  showDealLabel = false,
  position = 1,
}) => {
  const { formatPrice } = useCurrency();
  const { trackListingCardClick } = useAnalytics();

  const dealType = useMemo(() => {
    if (!listing.market_status?.price_ratio) return 'none';
    return evaluateDealType(listing.market_status.price_ratio);
  }, [listing.market_status?.price_ratio]);

  const searchParams = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search);
  }, []);

  const baseMetadata = useMemo(() => ({
    source: "listings_grid",
    position,
    search_query: searchParams?.get('q') || null,
    filters: searchParams ? Object.fromEntries(searchParams) : {},
    utm_source: searchParams?.get('utm_source') || null,
    utm_medium: searchParams?.get('utm_medium') || null,
    utm_campaign: searchParams?.get('utm_campaign') || null,
  }), [position, searchParams]);

  const impressionMetadata = useMemo(() => ({
    ...baseMetadata,
    show_deal_label: showDealLabel,
  }), [baseMetadata, showDealLabel]);

  const impressionRef = useImpressionTracking(listing, {
    threshold: 0.5,
    minVisibleTime: 1000,
    metadata: impressionMetadata
  });

  const handleListingClick = () => {
    trackListingCardClick(listing._id, listing.vendor, {
      ...baseMetadata,
      deal_type: dealType,
    });
  };

  const getMarketDifferenceText = () => {
    const soldAvg = listing.market_status?.sold_avg_price;
    const currAvg = listing.market_status?.avg_price;
    let avg = null;
    let avgType = '';

    if (soldAvg) {
      avg = soldAvg;
      avgType = 'Sold Market Average';
    } else if (currAvg) {
      avg = currAvg;
      avgType = 'Current Market Average';
    }

    if (!avg) return null;

    const diff = Math.round(listing.current_price - avg);
    const isAbove = diff > 0;
    const absDiff = Math.abs(diff);

    return {
      isAbove,
      amount: formatPrice(absDiff),
      direction: isAbove ? 'above' : 'below',
      avgType
    };
  };

  const marketDifference = getMarketDifferenceText();

  return (
    <div ref={impressionRef}>
      <a
        href={`/listings/${listing.ss_slug}`}
        className="block"
        onClick={handleListingClick}
      >
        <div className="aspect-square overflow-hidden relative">
          {listing.s3_image_urls.length > 0 && (
            <Image
              src={listing.s3_image_urls[0]}
              alt={`${listing.product.brand} ${listing.product.model}`}
              fill
              style={{ objectFit: 'cover' }}
              sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 20vw"
              loading="lazy"
            />
          )}

          {/* Deal Label */}
          {showDealLabel && dealType !== 'none' && (
            <DealLabel
              type={dealType}
              size="small"
              className="absolute top-3 left-3 px-1.5"
            />
          )}
          {/* Action Buttons */}
          <div className="absolute top-2 right-2 flex flex-col sm:flex-row gap-2">
            <FavoriteButton
              listing={listing}
              className=""
            />
            <PriceTrackingButton
              listing={listing}
              className=""
            />
          </div>
        </div>

        <div className="pt-6 px-4 pb-4 flex flex-col items-start text-left w-full">
          {/* Pre-owned Brand - first line, serif, bold */}
          <div className="mb-1 text-[#1C1C1C] font-[Bodoni_Moda] text-base font-normal leading-normal">
            Pre-owned {formatBrandName(listing.product.brand)}
          </div>
          {/* Product model/name */}
          <div className="mb-3 text-[#1C1C1C] font-raleway text-base font-normal leading-normal capitalize">
            {listing.product.model}
          </div>
          {/* Price */}
          <div className="mb-2 text-[#1C1C1C] font-raleway text-base font-normal leading-normal">
            {formatPrice(Math.round(listing.current_price))}
          </div>
          {marketDifference && (
            <div className="mb-2 text-[#6C6C6C] font-raleway text-xs font-normal leading-normal">
              Currently{' '}
              <span className={`font-raleway text-xs font-normal leading-normal ${marketDifference.isAbove ? 'text-[#FF2C2C]' : 'text-[#2E7D32]'}`}>
                {marketDifference.amount} {marketDifference.direction}
              </span>{' '}
              {marketDifference.avgType}
            </div>
          )}
        </div>
      </a>
    </div>
  );
};

export default ListingsGridCard;